/* ============================================================================
   guardado.js — adaptadores de autoguardado por sección.

   REGLA DEL REDISEÑO: cada sección se sigue guardando por EL MISMO endpoint que
   disparaba su botón «Actualizar». Aquí sólo se cambia el disparador (blur /
   debounce) por otro; el contrato con el backend es idéntico al de hoy.

   Todos devuelven `{ success: boolean, data?: any }`.
   ========================================================================== */
import {
  Controllerenfermendad,
  ControllerMotivoConsulta,
} from "../../../../Service/Service.consulta";
import { ControllerUpadateCreateSignosVitales } from "../../../../Service/Service.citas";
import {
  GuardarDiagnosticoPaciente,
  ControllerEliminarDiagnosticoPaciente,
} from "../../../../Service/Service.diagnostico";
import { ControllerAsignacionAntecendentePaciente } from "../../../../Service/Service.antecedente";

const ok = (res) => Boolean(res && res.success);

/** Motivo de consulta -> POST /consulta_motivo (citas.motivo). */
export async function guardarMotivo(ctx, motivo) {
  const res = await ControllerMotivoConsulta("consulta_motivo", {
    motivo,
    idcita: ctx.idcita,
    idpaciente: ctx.idpaciente,
    idclinica: ctx.idclinica,
  });
  return { success: ok(res), data: res?.data };
}

/** Enfermedad actual -> POST /consulta_enfermedad.
    OJO: la rama `id != 0` del backend fuerza id_estado_cita = 5 y responde 409
    si la cita no está en un estado apto. Ese comportamiento NO se toca. */
export async function guardarEnfermedad(ctx, texto, id) {
  const res = await Controllerenfermendad("consulta_enfermedad", {
    idenfermedad_actual_cita: id || 0,
    enfermedad_actual: texto,
    idcita: ctx.idcita,
    idpaciente: ctx.idpaciente,
    idclinica: ctx.idclinica,
  });
  return { success: ok(res), data: res?.data };
}

export async function guardarPlan(ctx, texto, id) {
  const res = await Controllerenfermendad("consulta_plantratamiento", {
    idplan_tratamiento: id || 0,
    plan_tratamiento: texto,
    idcita: ctx.idcita,
    idpaciente: ctx.idpaciente,
    idclinica: ctx.idclinica,
  });
  return { success: ok(res), data: res?.data };
}

export async function guardarNotaEvolucion(ctx, texto, id) {
  const res = await Controllerenfermendad("consulta_nota_evolucion", {
    idnota_evolucion: id || 0,
    nota_evolucion: texto,
    idcita: ctx.idcita,
    idpaciente: ctx.idpaciente,
    idclinica: ctx.idclinica,
  });
  return { success: ok(res), data: res?.data };
}

/** Signos vitales -> POST /signosvitales_create (upsert por idcita en backend). */
export async function guardarSignos(ctx, signos) {
  const res = await ControllerUpadateCreateSignosVitales({
    ...signos,
    idclinica: ctx.idclinica,
    idcita: ctx.idcita,
  });
  return { success: ok(res), data: res?.data };
}

/** Diagnósticos: alta / baja (los cambios de la sección son incrementales). */
export async function agregarDiagnostico(ctx, { iddiagnostico, comentario, typo_diagnostico }) {
  const res = await GuardarDiagnosticoPaciente({
    iddiagnostico_cita_paciente: 0,
    idcita: ctx.idcita,
    idpaciente: ctx.idpaciente,
    iddiagnostico,
    iddoctor: ctx.iddoctor,
    idclinica: ctx.idclinica,
    comentario: comentario || "",
    typo_diagnostico: typo_diagnostico || "",
  });
  return { success: ok(res), data: res?.data };
}

export async function eliminarDiagnostico(id) {
  const res = await ControllerEliminarDiagnosticoPaciente(id);
  return { success: ok(res), data: res?.data };
}

/** Revisión por sistemas / examen físico -> POST /asigancion_antecedente.
    Mapa de la interfaz nueva a los MISMOS valores CP/SP de siempre:
      Normal  -> sp = true,  cp = false
      Hallazgo-> cp = true,  sp = false
      (sin evaluar) -> cp = false, sp = false   (= estado "ambos apagados" de hoy)
    `tiene` se sigue mandando false, igual que el componente actual. */
export async function guardarHallazgo(ctx, fila) {
  const res = await ControllerAsignacionAntecendentePaciente(
    fila.id_antecedente,
    fila.observacion || null,
    ctx.idpaciente,
    false, // tiene
    fila.cp === true,
    fila.sp === true,
    ctx.idcita
  );
  return { success: ok(res), data: res?.data };
}
